// Opening a stored position on the real board.
//
// The gallery keeps two things per frame and they answer different questions.
// The **drawing** is what every card shows: cheap, readable by a publisher with
// no rules core in it, and lossy — a loaded lane has no direction in it, any
// heap over nine is a star, and nothing anybody is *carrying* appears at all.
// The **snapshot** is the state itself, and it is what makes the board openable
// rather than merely visible.
//
// So the panel prefers the snapshot and falls back to reading the drawing
// (`asciiBoard.ts`). The difference is the whole point of storing one: a
// reconstruction shows where the pieces stood, and this shows what they held.

import { decode } from "src/engine/game/codec";
import type { GameState } from "src/engine/rules/commands";
import { pieceColor, type PieceType, pieceType } from "src/engine/rules/board";
import { PIECE_NAMES } from "src/engine/rules/rules";
import { PIECE_GLYPH } from "src/engine/session/glyphs";
import { beltDirAt, liveSquares, mineableAt, mineableSquares, unitCount } from "src/engine/rules/industry/state";
import { rebuildStructureMirror } from "src/engine/rules/industry/structures";

/** base64 → bytes, without a dependency. */
function bytesOf(base64: string): Uint8Array {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Hydrate a stored snapshot, or null if it cannot be read.
 *
 * **Async because the decompression is**, and that is the one thing this shape
 * costs: `DecompressionStream` is the platform's gzip and there is no
 * synchronous form of it. Worth it at the size — measured 13KB of state down to
 * 1.9KB, which is the difference between shipping one openable game per army
 * and shipping none.
 *
 * Never throws. A snapshot that will not read is a board that falls back to the
 * drawing, which is exactly what every other card already does.
 */
export async function hydrate(base64: string): Promise<GameState | null> {
  try {
    const stream = new Blob([bytesOf(base64) as BlobPart])
      .stream()
      .pipeThrough(new DecompressionStream("gzip"));
    const json = await new Response(stream).text();
    const state = decode(JSON.parse(json)) as GameState;
    // The position's structure mirrors are derived state, so they are rebuilt
    // rather than trusted — a snapshot written before either existed decodes
    // to a position without the map at all, and the owner mirror (§26 F5) and
    // the health one (§27) are both read by move generation. `loadRun` does
    // the same thing for the same reason.
    rebuildStructureMirror(state.position, state.industry);
    return state;
  } catch {
    return null;
  }
}

/** What one army had, at the moment the snapshot was taken. */
export interface SideResources {
  side: number;
  /** Piece counts, each with the glyph the rest of the interface uses. */
  army: { name: string; glyph: string; count: number }[];
  pieces: number;
  /** Units in every bank of every piece it owns — the number a drawing hides. */
  held: number;
}

/**
 * The counts worth reading beside the board.
 *
 * `held` is the one that justifies the snapshot: it is invisible in a drawing,
 * and "this army ends its games sitting on forty units" is exactly the kind of
 * thing a person is looking for when they ask whether a style is behaving.
 */
export function resourcesOf(state: GameState): SideResources[] {
  const sides = new Map<number, SideResources>();
  const seat = (side: number): SideResources => {
    const held = sides.get(side) ?? { side, army: [], pieces: 0, held: 0 };
    sides.set(side, held);
    return held;
  };

  const counts = new Map<number, Map<number, number>>();
  for (const [square, piece] of state.position.board) {
    const side = pieceColor(piece);
    const row = seat(side);
    row.pieces++;
    for (const bank of ["in", "out"] as const) {
      row.held += unitCount(state.industry, square, bank);
    }
    const byType = counts.get(side) ?? new Map<number, number>();
    const type = pieceType(piece);
    byType.set(type, (byType.get(type) ?? 0) + 1);
    counts.set(side, byType);
  }

  for (const [side, byType] of counts) {
    // **Through `PIECE_GLYPH`, never an initial.** King and Knight both begin
    // with K, so a first letter renders "2K 1K" and says nothing about which
    // is which — and there is already one table for this, shared with the
    // strip that counts an army and the shelf that offers recipes.
    seat(side).army = [...byType.entries()]
      .map(([type, count]) => ({
        name: PIECE_NAMES[type as PieceType] ?? "?",
        glyph: PIECE_GLYPH[type as PieceType] ?? "?",
        count
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }
  return [...sides.values()].sort((a, b) => a.side - b.side);
}

/**
 * Parts on the board, whoever laid them.
 *
 * **Not per side**, and that is a fact about the state rather than a shortcut:
 * ownership lives on its own map, so counting the deck gives a board total.
 * Reported as one number rather than attributed to side 0, which is what it
 * looked like when it sat on that row.
 */
export function partsOn(state: GameState): number {
  let total = 0;
  for (const square of liveSquares(state.industry.beltDir)) {
    void beltDirAt(state.industry, square);
    total++;
  }
  return total;
}

/** Scrap still lying on the floor, which belongs to nobody. */
export function scrapOn(state: GameState): number {
  let total = 0;
  for (const square of mineableSquares(state.industry)) total += mineableAt(state.industry, square);
  return total;
}
